import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, X, FileText } from "../icons";
import {
  ROSTER_IMPORT_MAX_IMAGES,
  ROSTER_IMPORT_MAX_IMAGE_BYTES,
  validateRosterImportImage,
} from "../../lib/rosterImportApi";

interface PreviewEntry {
  file: File;
  url: string;
}

export interface ImageDropzoneProps {
  images: File[];
  onChange: (images: File[]) => void;
  disabled?: boolean;
}

/**
 * Squad-image input for the roster importer (EPIC-95 AC4): file picker,
 * drag/drop, and — the locked design's "first-class" input — pasting
 * straight from the clipboard (Ctrl/Cmd+V of a snipping-tool capture).
 *
 * The paste listener is on `window`, not a focusable element inside this
 * component: a snip-and-paste workflow has no reason to first click into a
 * particular box, and Cmd+V with nothing focused still dispatches a
 * document-level `paste` event. It is only attached while this component is
 * mounted (the "input" step of RosterImportPage), so it can never intercept
 * a paste anywhere else in the app.
 */
export default function ImageDropzone({
  images,
  onChange,
  disabled,
}: ImageDropzoneProps) {
  const { t } = useTranslation("admin");
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previews, setPreviews] = useState<PreviewEntry[]>([]);

  useEffect(() => {
    const next = images.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPreviews(next);
    return () => {
      next.forEach((entry) => URL.revokeObjectURL(entry.url));
    };
  }, [images]);

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (disabled || incoming.length === 0) return;
      const room = ROSTER_IMPORT_MAX_IMAGES - images.length;
      if (room <= 0) {
        setError(
          t("rosterImport.tooManyImages", {
            max: ROSTER_IMPORT_MAX_IMAGES,
          }),
        );
        return;
      }

      const accepted: File[] = [];
      let rejection: string | null = null;
      for (const file of incoming) {
        if (accepted.length >= room) {
          rejection = t("rosterImport.tooManyImages", {
            max: ROSTER_IMPORT_MAX_IMAGES,
          });
          break;
        }
        const kind = validateRosterImportImage(file);
        if (kind === "type") {
          rejection = t("rosterImport.errorImageType");
          continue;
        }
        if (kind === "size") {
          rejection = t("rosterImport.errorImageSize", {
            maxMb: Math.round(ROSTER_IMPORT_MAX_IMAGE_BYTES / (1024 * 1024)),
          });
          continue;
        }
        accepted.push(file);
      }

      if (accepted.length > 0) {
        onChange([...images, ...accepted]);
      }
      setError(rejection);
    },
    [disabled, images, onChange, t],
  );

  // Document-level paste (see the doc comment above for why not a local
  // onPaste handler): reads image items off the clipboard, ignores
  // everything else (plain text goes through the separate textarea).
  useEffect(() => {
    if (disabled) return;
    const handlePaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items || items.length === 0) return;
      const files: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        event.preventDefault();
        addFiles(files);
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [addFiles, disabled]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) addFiles(files);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeAt = (index: number) => {
    const next = images.slice();
    next.splice(index, 1);
    onChange(next);
    setError(null);
  };

  return (
    <div className="space-y-2">
      <div
        data-testid="roster-import-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
          dragOver
            ? "border-primary-400 bg-primary-50"
            : "border-gray-300 bg-gray-50 hover:border-gray-400"
        } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <Upload className="h-8 w-8 text-gray-400" aria-hidden="true" />
        <p className="text-sm font-medium text-gray-700">
          {t("rosterImport.dropHint")}
        </p>
        <p className="text-xs text-gray-500">{t("rosterImport.pasteHint")}</p>
        <p className="text-xs text-gray-400">
          {t("rosterImport.imageConstraints", {
            max: ROSTER_IMPORT_MAX_IMAGES,
            maxMb: Math.round(ROSTER_IMPORT_MAX_IMAGE_BYTES / (1024 * 1024)),
          })}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFileInput}
          disabled={disabled}
          className="hidden"
          data-testid="roster-import-file-input"
        />
      </div>

      {error && (
        <p
          className="text-sm text-red-600"
          data-testid="roster-import-image-error"
        >
          {error}
        </p>
      )}

      {previews.length > 0 && (
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          data-testid="roster-import-image-list"
        >
          {previews.map((entry, index) => (
            <div
              key={`${entry.file.name}-${entry.file.lastModified}-${index}`}
              className="relative rounded-lg border border-gray-200 bg-white p-1"
            >
              <img
                src={entry.url}
                alt={entry.file.name}
                className="h-24 w-full rounded object-cover"
              />
              <div className="mt-1 flex items-center gap-1 px-1">
                <FileText
                  className="h-3 w-3 text-gray-400 shrink-0"
                  aria-hidden="true"
                />
                <span className="truncate text-[10px] text-gray-500">
                  {entry.file.name}
                </span>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeAt(index)}
                  aria-label={t("rosterImport.removeImage")}
                  data-testid={`roster-import-image-remove-${index}`}
                  className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
